/**
 * Snippets (clips) data store — the library's contents. Panel open/close
 * state lives in modal-store ('snippets' key), not here, matching the split
 * modal-store documents for critique/generate/etc.
 */
import { create } from 'zustand';
import type { Snippet } from '@/types';
import { snippetStorage } from '@/services/snippet-storage';
import { showSuccessToast, showErrorToast } from './toast-store';

interface SnippetsState {
  snippets: Snippet[];
  isLoading: boolean;
  error: string | null;

  loadSnippets: () => Promise<void>;
  addSnippet: (snippet: Snippet) => Promise<void>;
  deleteSnippet: (id: string) => Promise<void>;
  renameSnippet: (id: string, name: string) => Promise<void>;
  updateCaption: (id: string, caption: string) => Promise<void>;
}

export const useSnippetsStore = create<SnippetsState>((set, get) => ({
  snippets: [],
  isLoading: false,
  error: null,

  loadSnippets: async () => {
    set({ isLoading: true, error: null });
    try {
      const snippets = await snippetStorage.getAll();
      set({ snippets, isLoading: false });
    } catch (error) {
      console.error('Failed to load snippets:', error);
      set({ error: 'Failed to load clips', isLoading: false });
    }
  },

  addSnippet: async (snippet) => {
    try {
      await snippetStorage.save(snippet);
      await get().loadSnippets();
    } catch (error) {
      console.error('Failed to save snippet:', error);
      showErrorToast("couldn't save that clip");
    }
  },

  deleteSnippet: async (id) => {
    try {
      await snippetStorage.delete(id);
      await get().loadSnippets();
    } catch (error) {
      console.error('Failed to delete snippet:', error);
      showErrorToast("couldn't delete that clip");
    }
  },

  renameSnippet: async (id, name) => {
    try {
      const snippet = await snippetStorage.getById(id);
      if (!snippet) return;
      await snippetStorage.save({ ...snippet, name });
      await get().loadSnippets();
    } catch (error) {
      console.error('Failed to rename snippet:', error);
      showErrorToast("couldn't rename that clip");
    }
  },

  updateCaption: async (id, caption) => {
    try {
      const snippet = await snippetStorage.getById(id);
      if (!snippet) return;
      await snippetStorage.save({ ...snippet, caption });
      await get().loadSnippets();
    } catch (error) {
      console.error('Failed to update caption:', error);
      showErrorToast("couldn't update that caption");
    }
  },
}));

/** Convenience for the capture flow: save + toast in one call. */
export async function clipSnippet(snippet: Snippet): Promise<void> {
  await useSnippetsStore.getState().addSnippet(snippet);
  showSuccessToast(`clipped — ${snippet.caption}`);
}
